"use client"
import {
    Accordion,
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    Input,
    Slider,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { useDcfWorkbenchData } from '../../hooks/useDcfWorkbenchData';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { createRating, getRatingUsage } from '../../api';
import {
    ratingInitialState,
    ratingReducer,
    usageInitialState,
    usageReducer,
} from './stockAnalysis/reducers';
import { ensureScreenshotCaptureStyles } from './stockAnalysis/screenshotUtils';
import StockAnalysisRatingPanel from './stockAnalysis/StockAnalysisRatingPanel';
import { useDcfController } from './stockAnalysis/useDcfController';
import { useDcfCalculation } from './stockAnalysis/useDcfCalculation';
import DcfScenarios from './stockAnalysis/DcfScenarios';
import DcfExhibit, { ExitMultipleSensitivity } from './stockAnalysis/DcfExhibit';
import { calculateExitMultipleValuation, projectionsFromApiRows } from './stockAnalysis/exitMultipleDcf';
import { formatCompact, parseAbbreviatedNumber } from './stockAnalysis/numberFormat';

ensureScreenshotCaptureStyles();

const StockAnalysisProjectionChart = lazy(() => import('./StockAnalysisProjectionChart'));

const loadingMessages = [
    'Reading the annual report...',
    'Checking the income statement...',
    'Analyzing free cash flow trends...',
    'Measuring the competitive moat...',
    'Evaluating management quality...',
    'Calculating intrinsic value...',
    'Channeling Warren Buffett...',
    'Looking for margin of safety...',
    'Reviewing the balance sheet...',
    'Almost there...',
];

function getFiniteNumber(...values) {
    for (const value of values) {
        if (value == null || value === '') continue;
        const number = Number(value);
        if (Number.isFinite(number)) return number;
    }
    return null;
}

function calculateHistoricalFreeCashFlow(entry) {
    const directFreeCashFlow = getFiniteNumber(entry.fcf, entry.free_cash_flow);
    if (directFreeCashFlow !== null) return directFreeCashFlow;

    const operatingCashFlow = getFiniteNumber(
        entry.ocf,
        entry.operating_cash_flow,
        entry.cash_flows_from_operations,
    );
    const capitalExpenditures = getFiniteNumber(
        entry.capex,
        entry.capital_expenditures,
        entry.capitalExpenditures,
    );

    if (operatingCashFlow === null) return null;
    return operatingCashFlow - (capitalExpenditures || 0);
}

function SectionCard({ id, title, description, action, children }) {
    return (
        <Box
            as="section"
            id={id}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
            bg="white"
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
        >
            <Flex
                align={{ base: 'flex-start', md: 'center' }}
                justify="space-between"
                gap={4}
                direction={{ base: 'column', md: 'row' }}
                mb={description ? 5 : 0}
            >
                <Box>
                    <Heading as="h2" size="lg">{title}</Heading>
                    {description && (
                        <Text color="gray.600" mt={1}>{description}</Text>
                    )}
                </Box>
                {action}
            </Flex>
            {children}
        </Box>
    );
}

function AssumptionInput({
    label,
    ariaLabel,
    value,
    field,
    dispatch,
    percent = false,
    abbreviated = false,
    currency = false,
    min,
    max,
    step,
    tooltip,
    commitOnBlur = false,
}) {
    const formattedValue = value == null
        ? ''
        : percent
            ? Number((value * 100).toFixed(2))
            : abbreviated
                ? formatCompact(value, { currency })
                : value;
    const [draftValue, setDraftValue] = useState(String(formattedValue));
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            setDraftValue(String(formattedValue));
        }
    }, [formattedValue, isFocused]);

    const parseRawValue = (rawValue) => {
        const text = String(rawValue).trim();
        if (text === '' || text === '-' || text === '.' || text === '-.') return null;
        return abbreviated ? parseAbbreviatedNumber(text) : Number(text);
    };

    const commitValue = (rawValue) => {
        const parsed = parseRawValue(rawValue);
        if (parsed == null || !Number.isFinite(parsed)) return;
        dispatch({
            type: 'set_field',
            field,
            value: percent ? parsed / 100 : parsed,
        });
    };

    return (
        <Box>
            <Flex align="center" gap={1} mb={2}>
                <Text fontWeight="semibold">{label}</Text>
                {tooltip}
            </Flex>
            <Input
                aria-label={ariaLabel || label}
                value={isFocused ? draftValue : String(formattedValue)}
                onChange={(event) => {
                    setDraftValue(event.target.value);
                    if (!commitOnBlur) commitValue(event.target.value);
                }}
                onFocus={() => {
                    setIsFocused(true);
                    setDraftValue(value == null ? '' : percent ? String(Number((value * 100).toFixed(2))) : String(value));
                }}
                onBlur={(event) => {
                    if (commitOnBlur) commitValue(event.target.value);
                    setIsFocused(false);
                    setDraftValue(String(formattedValue));
                }}
                type="text"
                inputMode={abbreviated ? 'text' : 'decimal'}
                min={min}
                max={max}
                step={step}
            />
            {percent && (
                <Slider.Root
                    mt={3}
                    value={[Number.isFinite(Number(formattedValue)) ? Number(formattedValue) : 0]}
                    min={min}
                    max={max}
                    step={step}
                    onValueChange={(event) => commitValue(event.value[0])}
                >
                    <Slider.Control>
                        <Slider.Track bg="gray.200">
                            <Slider.Range bg="green.500" />
                        </Slider.Track>
                        <Slider.Thumb index={0} />
                    </Slider.Control>
                </Slider.Root>
            )}
        </Box>
    );
}

function ModelAssumptionControls({ state, dispatch }) {
    return (
        <Grid
            templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
            gap={5}
            mb={6}
        >
            <AssumptionInput
                label="Forecast Years"
                value={state.forecastYears}
                field="forecastYears"
                dispatch={dispatch}
                min={1}
                max={30}
                step={1}
                commitOnBlur
            />
            <AssumptionInput
                label="Discount Rate"
                value={state.wacc}
                field="wacc"
                dispatch={dispatch}
                percent
                min={1}
                max={30}
                step={0.25}
            />
            <AssumptionInput
                label="FCF Growth Rate"
                value={state.fcfGrowthRate}
                field="fcfGrowthRate"
                dispatch={dispatch}
                percent
                min={-20}
                max={50}
                step={0.5}
            />
        </Grid>
    );
}

function ProjectionChart({ history, valuation }) {
    const historicalPoints = history
        .filter((entry) => Number.isFinite(Number(entry.fcf)))
        .map((entry) => ({
            label: String(entry.year),
            historicalFcf: entry.fcf,
            projectedFcff: null,
            projectedFcfPv: null,
            section: 'historical',
        }));
    const projectedPoints = (valuation?.rows || [])
        .filter((entry) => Number.isFinite(Number(entry.unleveredFreeCashFlow)))
        .map((entry) => ({
            label: `Year ${entry.year}`,
            historicalFcf: null,
            projectedFcff: entry.unleveredFreeCashFlow,
            projectedFcfPv: entry.presentValue,
            section: 'projection',
        }));

    if (historicalPoints.length === 0 || projectedPoints.length === 0) return null;

    return (
        <Suspense fallback={<Flex justify="center" py={8}><Spinner color="green.500" /></Flex>}>
            <StockAnalysisProjectionChart
                series={[...historicalPoints, ...projectedPoints]}
                projectionStart={projectedPoints[0].label}
            />
        </Suspense>
    );
}

function StockAnalysis() {
    const { ticker } = useParams();
    const normalizedTicker = ticker ? ticker.toUpperCase() : '';
    const {
        loading,
        error,
        driverHistory,
        defaults,
        baseRevenue,
        netDebt,
        debt,
        cash,
        shares,
        price,
    } = useDcfWorkbenchData(ticker);
    const { state: dcfState, dispatch: dispatchDcf } = useDcfController({
        defaults,
        baseRevenue,
        netDebt,
        totalDebt: debt,
        cash,
        shares,
    });
    const {
        result: dcfResult,
        loading: isDcfLoading,
        error: dcfError,
    } = useDcfCalculation(dcfState);
    const {
        isAuthenticated,
        isLoading: isAuthLoading,
        getAccessTokenSilently,
        loginWithRedirect,
    } = useAuth0();
    const [
        {
            result: ratingResult,
            isLoading: isRatingLoading,
            error: ratingError,
            loadingMessageIndex,
            loadingProgress,
        },
        dispatchRating,
    ] = useReducer(ratingReducer, ratingInitialState);
    const [{ data: usage, isLoading: isUsageLoading, error: usageError }, dispatchUsage] = useReducer(
        usageReducer,
        usageInitialState
    );
    const loadingProgressRef = useRef(loadingProgress);

    useEffect(() => {
        loadingProgressRef.current = loadingProgress;
    }, [loadingProgress]);

    useEffect(() => {
        if (!isRatingLoading) return undefined;

        const messageInterval = setInterval(() => {
            dispatchRating({ type: 'tick_message', messageCount: loadingMessages.length });
        }, 2000);

        const progressInterval = setInterval(() => {
            dispatchRating({ type: 'tick_progress' });
        }, 800);

        return () => {
            clearInterval(messageInterval);
            clearInterval(progressInterval);
        };
    }, [isRatingLoading]);

    useEffect(() => {
        if (isRatingLoading || loadingProgressRef.current <= 0) return undefined;

        dispatchRating({ type: 'complete_progress' });
        const resetTimeout = setTimeout(() => {
            dispatchRating({ type: 'reset_progress' });
        }, 500);

        return () => clearTimeout(resetTimeout);
    }, [isRatingLoading]);

    const handleGenerateRating = async () => {
        if (!normalizedTicker) return;
        dispatchRating({ type: 'start' });
        try {
            const token = await getAccessTokenSilently();
            const response = await createRating(normalizedTicker, token);
            if (!response?.success || !response?.rating) {
                throw new Error('Could not generate rating.');
            }
            dispatchRating({ type: 'success', payload: response.rating });
            toast.success('AI rating generated!');
            await refreshUsage();
        } catch (err) {
            console.error('Failed to generate rating', err);
            const message = err?.response?.data?.message || err.message || 'Unable to generate rating.';
            dispatchRating({ type: 'error', payload: message });
            toast.error('Unable to generate AI rating at the moment.');
        }
    };

    const history = driverHistory
        .filter((entry) => entry.year <= new Date().getFullYear())
        .sort((a, b) => a.year - b.year)
        .slice(-10)
        .map((entry) => ({
            ...entry,
            fcf: calculateHistoricalFreeCashFlow(entry),
        }));

    useEffect(() => {
        const latestFcf = [...history]
            .reverse()
            .find((entry) => Number.isFinite(Number(entry.fcf)))
            ?.fcf;
        if (latestFcf == null) return;
        dispatchDcf({ type: 'set_starting_fcf_default', value: latestFcf });
    }, [history, dispatchDcf]);

    useEffect(() => {
        if (!dcfResult?.rows?.length) return;
        dispatchDcf({
            type: 'sync_forecast_rows',
            rows: projectionsFromApiRows(dcfResult.rows, { taxRate: dcfState.taxRate }),
        });
    }, [dcfResult, dcfState.taxRate, dispatchDcf]);

    const valuation = useMemo(() => calculateExitMultipleValuation({
        projections: dcfState.projections,
        wacc: dcfState.wacc,
        exitMultiple: dcfState.exitMultiple,
        totalDebt: dcfState.totalDebt,
        preferredStock: dcfState.preferredStock,
        noncontrollingInterest: dcfState.noncontrollingInterest,
        cash: dcfState.cash,
        sharesOutstanding: dcfState.sharesOutstanding,
    }), [dcfState]);

    const refreshUsage = useCallback(async () => {
        if (!isAuthenticated) {
            dispatchUsage({ type: 'clear' });
            return;
        }
        dispatchUsage({ type: 'load_start' });
        try {
            const token = await getAccessTokenSilently();
            const data = await getRatingUsage(token);
            dispatchUsage({ type: 'load_success', payload: data });
        } catch (err) {
            console.error('Failed to load rating usage', err);
            dispatchUsage({ type: 'load_error', payload: 'Unable to load rating usage right now.' });
        }
    }, [getAccessTokenSilently, isAuthenticated]);

    useEffect(() => {
        if (!isAuthLoading) {
            refreshUsage();
        }
    }, [refreshUsage, isAuthLoading]);

    const isPremiumMember = usage?.is_premium === true;
    const usedRatings = usage?.used_this_month ?? 0;
    const monthlyLimit = isPremiumMember ? null : usage?.monthly_limit ?? 3;
    const canGenerateRating = usage?.can_generate !== false;
    const renewalDate = usage?.subscription_renewal_date ?? null;
    const renewalLabel = renewalDate
        ? new Date(renewalDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : null;
    const usageLabel = usage
        ? isPremiumMember
            ? `Premium member — unlimited ratings${renewalLabel ? ` • Renews ${renewalLabel}` : ''}`
            : `${usedRatings}/${monthlyLimit} ratings used this month`
        : isUsageLoading
            ? 'Checking usage...'
            : 'Usage data unavailable';
    const ratingButtonDisabled = isRatingLoading || (usage && usage.can_generate === false);
    const reverseDcfPath = `/stock/${String(ticker || '').toLowerCase()}/reverse-dcf`;

    return (
        <Flex direction="column" gap={6} w="100%" pb={8} pr={{ base: 0, lg: 2 }}>
            <SectionCard
                id="model"
                title="DCF Analysis"
                description="Reported years stay fixed. Edit any forecast-year input, including revenue. EBIT, unlevered free cash flow, present value, and the implied share price stay calculated."
                action={(
                    <Button as={RouterLink} to={reverseDcfPath} variant="outline" colorPalette="green" alignSelf={{ base: 'flex-end', md: 'center' }}>
                        Open Reverse DCF
                    </Button>
                )}
            >
                <ModelAssumptionControls state={dcfState} dispatch={dispatchDcf} />
                <DcfExhibit
                    history={history}
                    apiRows={dcfResult?.rows}
                    projections={dcfState.projections}
                    projectionsCustomized={dcfState.projectionsCustomized}
                    loading={(loading || isDcfLoading) && !dcfState.projections?.length}
                    error={dcfError}
                    taxRate={dcfState.taxRate}
                    wacc={dcfState.wacc}
                    exitMultiple={dcfState.exitMultiple}
                    totalDebt={dcfState.totalDebt}
                    preferredStock={dcfState.preferredStock}
                    noncontrollingInterest={dcfState.noncontrollingInterest}
                    cash={dcfState.cash}
                    sharesOutstanding={dcfState.sharesOutstanding}
                    currentPrice={price?.value}
                    dispatch={dispatchDcf}
                />
                <ProjectionChart history={history} valuation={valuation} />
            </SectionCard>

            <SectionCard id="valuation" title="Valuation" description="Implied share price across discount rates and exit multiples.">
                <ExitMultipleSensitivity
                    projections={dcfState.projections}
                    wacc={dcfState.wacc}
                    exitMultiple={dcfState.exitMultiple}
                    totalDebt={dcfState.totalDebt}
                    preferredStock={dcfState.preferredStock}
                    noncontrollingInterest={dcfState.noncontrollingInterest}
                    cash={dcfState.cash}
                    sharesOutstanding={dcfState.sharesOutstanding}
                />
            </SectionCard>

            <SectionCard id="scenarios" title="Scenarios" description="Save, load, and compare sets of DCF assumptions.">
                <DcfScenarios
                    ticker={normalizedTicker}
                    assumptions={dcfState}
                    dispatch={dispatchDcf}
                    currentPrice={price?.value}
                    isAuthenticated={isAuthenticated}
                    getAccessTokenSilently={getAccessTokenSilently}
                    loginWithRedirect={loginWithRedirect}
                    calculationLoading={isDcfLoading}
                    calculationError={dcfError}
                />
            </SectionCard>

            <Box as="section" id="ai-rating">
                <Accordion.Root collapsible defaultValue={['ai-rating']} borderWidth="1px" borderRadius="xl" bg="white">
                    <Accordion.Item value="ai-rating" border="0">
                        <Accordion.ItemTrigger p={{ base: 4, md: 6 }}>
                            <Box flex="1" textAlign="left">
                                <Heading as="h2" size="lg">AI Rating</Heading>
                                <Text color="gray.600" mt={1}>Generate the existing teacher-grade qualitative rating.</Text>
                            </Box>
                            <Accordion.ItemIndicator />
                        </Accordion.ItemTrigger>
                        <Accordion.ItemContent>
                            <Accordion.ItemBody px={{ base: 4, md: 6 }} pb={{ base: 4, md: 6 }}>
                        <StockAnalysisRatingPanel
                            normalizedTicker={normalizedTicker}
                            authState={{ authenticated: isAuthenticated }}
                            usageState={{
                                data: usage,
                                loading: isUsageLoading,
                                error: usageError,
                                label: usageLabel,
                                usedRatings,
                                monthlyLimit,
                                premiumMember: isPremiumMember,
                                renewalDate,
                                canGenerate: canGenerateRating,
                            }}
                            ratingState={{
                                result: ratingResult,
                                loading: isRatingLoading,
                                error: ratingError,
                                buttonDisabled: ratingButtonDisabled,
                            }}
                            loadingState={{
                                messageIndex: loadingMessageIndex,
                                progress: loadingProgress,
                                messages: loadingMessages,
                            }}
                            onGenerateRating={handleGenerateRating}
                        />
                            </Accordion.ItemBody>
                        </Accordion.ItemContent>
                    </Accordion.Item>
                </Accordion.Root>
            </Box>
        </Flex>
    );
}

export default StockAnalysis;
